import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight, Linkedin, Facebook, Instagram, MessageCircle, Youtube } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// X (Twitter) icon component
const XIcon = ({ size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Messenger icon component
const MessengerIcon = ({ size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.13.26.35.27.57l.05 1.78c.04.57.61.94 1.13.71l1.98-.87c.17-.08.36-.1.53-.06.91.25 1.87.38 2.9.38 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.73l-2.91 4.6c-.46.73-1.46.92-2.17.42l-2.32-1.73c-.22-.16-.52-.16-.74 0l-3.13 2.38c-.42.32-.97-.18-.69-.62l2.91-4.6c.46-.73 1.46-.92 2.17-.42l2.32 1.73c.22.16.52.16.74 0l3.13-2.38c.42-.32.97.18.69.62z"/>
  </svg>
);

// TikTok icon component
const TikTokIcon = ({ size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Social icon mapping
const SOCIAL_ICONS = {
  linkedin: Linkedin,
  facebook: Facebook,
  twitter: XIcon,
  instagram: Instagram,
  whatsapp: MessageCircle,
  messenger: MessengerIcon,
  youtube: Youtube,
  tiktok: TikTokIcon
};

const Footer = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const [partners, setPartners] = useState([]);
  const [socialLinks, setSocialLinks] = useState({});
  // Load from cache first for instant display
  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('site_settings');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [footerSettings, setFooterSettings] = useState({
    description_en: "Specialist in industrial and agricultural spare parts and equipment since 1998.",
    description_fr: "Spécialiste du matériel et de la pièce de rechange industriels et agricoles depuis 1998.",
    description_ar: "متخصصون في المعدات وقطع الغيار الزراعية والصناعية منذ 1998.",
    address: "USTO - ORAN, Algeria",
    phone: "+213 (41) 706 906",
    email: "contact@sae-dz.com",
    hours_weekdays_en: "Sun - Thu",
    hours_weekdays_fr: "Dim - Jeu",
    hours_weekdays_ar: "الأحد - الخميس",
    hours_weekdays_time: "8:00 - 17:00",
    hours_weekend_en: "Fri - Sat",
    hours_weekend_fr: "Ven - Sam",
    hours_weekend_ar: "الجمعة - السبت",
    hours_weekend_status_en: "Closed",
    hours_weekend_status_fr: "Fermé",
    hours_weekend_status_ar: "مغلق"
  });

  useEffect(() => {
    // Fetch partners
    fetch(`${BACKEND_URL}/api/partners`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setPartners(data.slice(0, 6));
        }
      })
      .catch(err => console.error('Error fetching partners:', err));

    // Fetch footer settings
    fetch(`${BACKEND_URL}/api/footer-settings`)
      .then(res => res.json())
      .then(data => {
        if (data) {
          setFooterSettings(data);
        }
      })
      .catch(err => console.error('Error fetching footer settings:', err));
      
    // Fetch site settings (logo) and cache
    fetch(`${BACKEND_URL}/api/site-settings`)
      .then(res => res.json())
      .then(data => {
        setSiteSettings(data);
        try { localStorage.setItem('site_settings', JSON.stringify(data)); } catch {}
      })
      .catch(() => {});

    // Fetch social links
    fetch(`${BACKEND_URL}/api/social-links`)
      .then(res => res.json())
      .then(data => {
        if (data) {
          setSocialLinks(data);
        }
      })
      .catch(err => console.error('Error fetching social links:', err));
  }, []);

  // Get active social links
  const activeSocialLinks = Object.entries(socialLinks)
    .filter(([key, value]) => value?.is_active && value?.url)
    .sort((a, b) => (a[1].order || 0) - (b[1].order || 0));

  // Scroll to top on navigation
  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-950 text-white">
      {/* Partners strip */}
      <div className="border-b border-slate-800">
        <div className="container mx-auto px-6 py-12">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-8 text-center">
            {language === 'ar' ? 'الممثل الرسمي لـ' : language === 'en' ? 'Official Representative of' : 'Représentant Officiel de'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-12">
            {partners.map((partner) => (
              <Link
                key={partner.id}
                to={`/partner/${partner.id}`}
                onClick={handleNavClick}
                className="opacity-50 hover:opacity-100 transition-opacity duration-300"
              >
                <img 
                  src={partner.logo} 
                  alt={partner.name} 
                  className="h-8 w-auto"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            {(siteSettings?.logo_footer || siteSettings?.logo_header) && (
              <img 
                src={siteSettings.logo_footer || siteSettings.logo_header} 
                alt={siteSettings.logo_alt || "SAE"} 
                className="h-14 w-auto mb-6 object-contain"
              />
            )}
            <p className="text-slate-400 text-sm leading-relaxed">
              {language === 'ar' 
                ? (footerSettings.description_ar || footerSettings.description_fr)
                : language === 'en' 
                  ? footerSettings.description_en 
                  : footerSettings.description_fr}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-6">
              {language === 'ar' ? 'التنقل' : language === 'en' ? 'Navigation' : 'Navigation'}
            </h4>
            <ul className="space-y-3">
              {[t('nav.equipment'), t('nav.services'), t('nav.partners'), t('nav.references'), t('nav.contact')].map((item, idx) => (
                <li key={idx}>
                  <Link 
                    to={`/${['equipment', 'services', 'partners', 'references', 'contact'][idx]}`} 
                    onClick={handleNavClick}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {item}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-6">
              {language === 'ar' ? 'اتصل بنا' : language === 'en' ? 'Contact' : 'Contact'}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-slate-400">
                <MapPin size={16} className="text-slate-500 flex-shrink-0 mt-1" />
                <a 
                  href="https://maps.app.goo.gl/GPaMHSSiUcEEy97D6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {footerSettings.address}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Phone size={16} className="text-slate-500 flex-shrink-0" />
                <a href={`tel:${footerSettings.phone?.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                  {footerSettings.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Mail size={16} className="text-slate-500 flex-shrink-0" />
                <a href={`mailto:${footerSettings.email}`} className="hover:text-white transition-colors">
                  {footerSettings.email}
                </a>
              </li>
            </ul>
            
            {/* Social Links */}
            {activeSocialLinks.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-800">
                <h5 className="text-xs uppercase tracking-widest text-slate-500 mb-4">
                  {language === 'ar' ? 'تابعونا' : language === 'en' ? 'Follow Us' : 'Suivez-nous'}
                </h5>
                <div className="flex items-center gap-3">
                  {activeSocialLinks.map(([platform, data]) => {
                    const Icon = SOCIAL_ICONS[platform];
                    if (!Icon) return null;
                    return (
                      <a
                        key={platform}
                        href={data.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all"
                        title={platform.charAt(0).toUpperCase() + platform.slice(1)}
                      >
                        <Icon size={18} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-6">
              {language === 'ar' ? 'ساعات العمل' : language === 'en' ? 'Hours' : 'Horaires'}
            </h4>
            <div className="text-sm text-slate-400 space-y-2">
              <p>
                <span className="text-white">
                  {language === 'ar' 
                    ? (footerSettings.hours_weekdays_ar || footerSettings.hours_weekdays_fr)
                    : language === 'en' 
                      ? footerSettings.hours_weekdays_en 
                      : footerSettings.hours_weekdays_fr}
                </span><br />
                {footerSettings.hours_weekdays_time}
              </p>
              <p>
                <span className="text-white">
                  {language === 'ar' 
                    ? (footerSettings.hours_weekend_ar || footerSettings.hours_weekend_fr)
                    : language === 'en' 
                      ? footerSettings.hours_weekend_en 
                      : footerSettings.hours_weekend_fr}
                </span><br />
                {language === 'ar' 
                  ? (footerSettings.hours_weekend_status_ar || footerSettings.hours_weekend_status_fr)
                  : language === 'en' 
                    ? footerSettings.hours_weekend_status_en 
                    : footerSettings.hours_weekend_status_fr}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              © {currentYear} SAE Agro Equipment. {t('footer.rights')}.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
